import * as addressActions from '~/actions/address.actions';
import { ActionKeys } from '~/actions/constants';
import { sendTrackEvent } from '~/actions/prod-analytics.actions';
import * as userActions from '~/actions/user.actions';
import axios, { AxiosResponse } from 'axios';
import { LocationDescriptorObject } from 'history';
import { AddressBookInstances } from '~/reducers/address/address.reducer';
import { call, put, takeLatest } from 'redux-saga/effects';
import { getAddressBookInstances } from '~/selectors/address.selectors';
import { getLocation } from '~/selectors/router.selectors';
import * as addressService from '~/services/address.service';
import { sanitizeAddress } from '~/types/Address';
import * as APIAddress from '~/types/APIAddress';
import { selectState } from '..';
import {
  getGenericErrorsFromResponseData,
  requestUpdatedTrack,
  trackRequestedUpdatedAxiosError,
} from '~/utils/TrackingHelper/utils';

export function* fetchAddresses() {
  try {
    const response: AxiosResponse<{ results: APIAddress.JSONAPIAddress[] }> = yield call(
      addressService.getApiAddresses,
    );
    const parsedAddresses: addressActions.FetchAddressesPayload = {
      addresses: response.data.results.map((a) => APIAddress.fromJSON(a)),
    };
    yield put(addressActions.fetchAddressesSuccess(parsedAddresses));
  } catch (err) {
    yield put(addressActions.fetchAddressesError({ error: (err as Error).message }));
  }
}

export function* createAPIAddress(action: ReturnType<typeof addressActions.createAPIAddress>) {
  const { trackingReference } = action.payload;
  try {
    const jsonAddress = APIAddress.toJSON(sanitizeAddress(action.payload.address))!;
    const response: AxiosResponse<APIAddress.JSONAPIAddress> = yield call(
      addressService.createApiAddress,
      jsonAddress,
    );

    requestUpdatedTrack(trackingReference, {
      url: response.config.url || '',
      isError: response.status >= 300,
      status: response.status,
      message: undefined,
    });

    const address = APIAddress.fromJSON(response.data)!;
    const { isDefaultSender, isDefaultReturn } = address;
    if (isDefaultSender) {
      yield put(userActions.updateUserDefaultAddress({ address }));
    }
    if (isDefaultReturn) {
      yield put(userActions.updateUserDefaultReturnAddress({ address }));
    }
    yield put(
      addressActions.createAPIAddressSuccess({
        address,
        trackingReference,
      }),
    );

    if (action.payload.key && action.payload.addressBookName) {
      yield put(
        addressActions.addressBookSelectAddress(
          action.payload.addressBookName,
          action.payload.key,
          address.id!,
        ),
      );
    }
  } catch (err) {
    if (axios.isAxiosError(err)) {
      trackRequestedUpdatedAxiosError(trackingReference, err);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = (err as any).response.data;
    const errorMessage =
      typeof responseData === 'object'
        ? Object.values(responseData).join('\n')
        : Array.isArray(responseData)
          ? responseData.join('\n')
          : responseData;
    yield put(addressActions.createAPIAddressError({ error: errorMessage }));
  }
}

export function* editAPIAddress(action: ReturnType<typeof addressActions.editAPIAddress>) {
  const { trackingReference } = action.payload;
  try {
    const jsonAddress: APIAddress.JSONAPIAddress = APIAddress.toJSON(
      sanitizeAddress(action.payload.address),
    )!;
    const response: Awaited<ReturnType<typeof addressService.editApiAddress>> = yield call(
      addressService.editApiAddress,
      action.payload.id!,
      jsonAddress,
    );

    // calls from address book do not have tracking reference
    trackingReference &&
      requestUpdatedTrack(trackingReference, {
        url: response.config.url || '',
        isError: response.status >= 300,
        status: response.status,
        message: undefined,
      });

    const address = APIAddress.fromJSON(response.data)!;
    const { isDefaultSender, isDefaultReturn } = address;
    const location: LocationDescriptorObject =
      yield selectState<LocationDescriptorObject>(getLocation);
    if (isDefaultSender) {
      if (location.search?.includes('progressMeter')) {
        yield put(sendTrackEvent('Progress Meter - Add Saved Address Complete'));
      }
      yield put(userActions.updateUserDefaultAddress({ address }));
    }
    if (isDefaultReturn) {
      yield put(userActions.updateUserDefaultReturnAddress({ address }));
    }
    yield put(addressActions.editAPIAddressSuccess({ address, id: action.payload.id }));
  } catch (err) {
    // calls from address book do not have tracking reference
    if (trackingReference && axios.isAxiosError(err) && err.response) {
      const { response } = err;
      requestUpdatedTrack(trackingReference, {
        url: response.config.url || '',
        isError: response.status >= 300,
        status: response.status,
        message: getGenericErrorsFromResponseData(response.data) || err.message,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const responseData = (err as any).response.data;
    const errorMessage =
      typeof responseData === 'object'
        ? Object.values(responseData).join('\n')
        : Array.isArray(responseData)
          ? responseData.join('\n')
          : responseData;
    yield put(addressActions.editAPIAddressError({ error: errorMessage }));
  }
}

export function* deleteAPIAddress(action: ReturnType<typeof addressActions.deleteAPIAddress>) {
  const { trackingReference } = action.payload;
  try {
    const deleteResponse: Awaited<
      AxiosResponse<ReturnType<typeof addressService.deleteApiAddress>>
    > = yield call(addressService.deleteApiAddress, action.payload.id);

    requestUpdatedTrack(trackingReference, {
      url: deleteResponse.config.url || '',
      isError: false,
      status: deleteResponse.status,
      message: undefined,
    });

    yield put(addressActions.deleteAPIAddressSuccess(action.payload.id));
    if (action.payload.key) {
      const addressBookInstances: AddressBookInstances =
        yield selectState<AddressBookInstances>(getAddressBookInstances);
      for (const addressBookName in addressBookInstances) {
        yield put(
          addressActions.addressBookSelectDefaultAddress(addressBookName, action.payload.key),
        );
      }
    }
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      requestUpdatedTrack(trackingReference, {
        url: err.response?.config.url || '',
        isError: true,
        status: err.response?.status,
        message: getGenericErrorsFromResponseData(err.response?.data) || err.message,
      });
    }
    yield put(addressActions.deleteAPIAddressError({ error: (err as Error).message }));
  }
}

export default [
  takeLatest(ActionKeys.ADDRESSES_FETCH, fetchAddresses),
  takeLatest(ActionKeys.APIADDRESS_CREATE, createAPIAddress),
  takeLatest(ActionKeys.APIADDRESS_EDIT, editAPIAddress),
  takeLatest(ActionKeys.APIADDRESS_DELETE, deleteAPIAddress),
];
